import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { Goals } from '../lib/types';
import { WorkPlanList, useWorkPlan } from './WorkPlan';
import { useCan } from '../lib/auth';
import { t } from '../i18n';
import { useDashboard } from '../lib/queries';
import { fmtDate, fmtMoney, fmtRelative, fmtDateTime } from '../lib/format';
import type { AgentStatus } from '../lib/types';
import { Callout, ErrorState } from '../components/ui';
import { agentTone } from '../components/Layout';
import { IconPlus, IconUpload } from '../components/icons';
import { UploadButton } from '../components/UploadDialog';
import { MarginBadge, ProfitKpi, profitTitle, useProfitSummary } from '../components/Margin';
import { fmtMonth } from '../lib/format';

function GoalCard() {
  const { data } = useQuery({ queryKey: ['goals'], queryFn: () => api<Goals>('/api/goals') });
  const sales = data?.month.items.find((i) => i.metric === 'sales');
  const pace = data ? Math.round((data.month.elapsedDays / data.month.totalDays) * 100) : 0;
  return (
    <section className="panel">
      <div className="panel-head">
        <h2>יעד מכירות החודש</h2>
        <span className="spacer" />
        <Link to="/goals" className="small">{sales?.target != null ? 'יעדים' : 'הגדרת יעד'}</Link>
      </div>
      <div className="panel-body stack" style={{ gap: 8 }}>
        {!sales ? (
          <span className="skeleton" style={{ width: '60%' }} />
        ) : sales.target == null ? (
          <p className="small muted">
            נמכר עד היום {fmtMoney(sales.actual, { round: true })} לפני מע״מ · צפי לסוף החודש {fmtMoney(sales.projected, { round: true })}.
            <br />
            <Link to="/goals">הגדרת יעד חודשי</Link> תאפשר לראות אם אתם בקצב.
          </p>
        ) : (
          <>
            <div className="row">
              <span className="kpi-value" style={{ fontSize: 20 }}>{fmtMoney(sales.actual, { round: true })}</span>
              <span className="muted small">מתוך {fmtMoney(sales.target, { round: true })}</span>
            </div>
            <div className={`progress ${sales.onTrack === false ? 'behind' : ''}`}>
              <span style={{ width: `${Math.min(100, sales.pct ?? 0)}%` }} />
              <i className="pace" style={{ insetInlineStart: `${pace}%` }} />
            </div>
            <div className="small" style={{ color: sales.onTrack === false ? 'var(--warn)' : 'var(--ok)' }}>
              {sales.pct}% · צפי {fmtMoney(sales.projected, { round: true })}
              {sales.onTrack === false && sales.neededPerDay != null ? ` · נדרש ${fmtMoney(sales.neededPerDay, { round: true })} ליום` : ' · בקצב'}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export function DashboardPage() {
  const { data, isLoading, error, refetch } = useDashboard();
  const can = useCan();
  const plan = useWorkPlan(can('sales.work'));
  const showMoney = can('finance.view');
  const month = new Date().toLocaleDateString('he-IL', { month: 'long', year: 'numeric' });

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t.nav.dashboard}</h1>
          <div className="sub">{new Date().toLocaleDateString('he-IL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</div>
        </div>
        <div className="row">
          {can('documents.edit') ? (
            <UploadButton>
              <IconUpload /> {t.profile.upload}
            </UploadButton>
          ) : null}
          {can('customers.edit') ? (
            <Link to="/customers/new" className="btn primary">
              <IconPlus /> {t.customers.new}
            </Link>
          ) : null}
        </div>
      </div>

      {error ? (
        <div className="panel"><ErrorState error={error} onRetry={() => refetch()} /></div>
      ) : (
        <div className="stack">
          {data ? <AgentBanner agent={data.agent} /> : null}

          {/* Money first: that's what the business owner scans for. */}
          {showMoney ? (
          <section className="kpis" aria-label="כספים">
            <Kpi label="יתרה פתוחה ללקוחות" value={fmtMoney(data?.finance?.outstanding, { round: true })} meta={data ? `${data.finance!.openInvoiceCount} חשבוניות פתוחות` : undefined} to="/finance" loading={isLoading} />
            <Kpi
              label="באיחור"
              value={fmtMoney(data?.finance?.overdue, { round: true })}
              meta={data ? `${data.finance!.overdueCount} חשבוניות` : undefined}
              tone={data && (data.finance!.overdue ?? 0) > 0 ? 'bad' : undefined}
              to="/finance"
              loading={isLoading}
            />
            <Kpi label={`חויב ב${month}`} value={fmtMoney(data?.finance?.invoicedThisMonth, { round: true })} meta={data ? `${data.documents.invoicesThisMonth} חשבוניות` : undefined} loading={isLoading} />
            <Kpi label="התקבל החודש" value={fmtMoney(data?.finance?.receivedThisMonth, { round: true })} meta="לפי קבלות שהופקו" loading={isLoading} />
            <Kpi label="חויב השנה" value={fmtMoney(data?.finance?.invoicedThisYear, { round: true })} meta="בניכוי זיכויים" loading={isLoading} />
          </section>
          ) : null}

          {can('profit.view') ? <ProfitPanel /> : null}

          <section className="kpis" aria-label="פעילות">
            <Kpi label="לקוחות" value={data?.customers.total} meta={data ? `${data.customers.active} פעילים · ${data.customers.prospects} פוטנציאליים` : undefined} to="/customers" loading={isLoading} />
            <Kpi label="לקוחות חדשים (30 יום)" value={data?.customers.new30} loading={isLoading} />
            <Kpi label="הצעות מחיר החודש" value={data?.documents.quotesThisMonth} meta={data ? fmtMoney(data.documents.quotesThisMonthTotal, { round: true }) : undefined} to="/documents?types=quote" loading={isLoading} />
            <Kpi label="תעודות משלוח החודש" value={data?.documents.deliveryNotesThisMonth} meta={data ? `${data.documents.unbilledDeliveryNotes} טרם חויבו` : undefined} to="/documents?types=delivery_note&unbilled=1" loading={isLoading} />
            <Kpi
              label="ממתינים לבדיקה"
              value={data?.agent.counters.needsReview}
              meta={data ? `${data.agent.counters.processed} מסמכים עובדו` : undefined}
              tone={data && data.agent.counters.needsReview > 0 ? 'warn' : undefined}
              to="/review"
              loading={isLoading}
            />
          </section>

          {can('sales.work') || showMoney ? (
          <div className="grid-main-side">
            {can('sales.work') ? (
            <section className="panel">
              <div className="panel-head">
                <h2>סדר עבודה להיום</h2>
                <span className="spacer" />
                <Link to="/today" className="small">לכל המשימות</Link>
              </div>
              {plan.data ? <WorkPlanList items={plan.data.daily} limit={5} /> : <div className="panel-body"><span className="skeleton" style={{ width: '70%' }} /></div>}
            </section>
            ) : <div />}
            {showMoney ? <GoalCard /> : null}
          </div>
          ) : null}

          <div className="grid-main-side">
            <section className="panel">
              <div className="panel-head">
                <h2>פעילות אחרונה</h2>
              </div>
              {data && data.recentActivity.length === 0 ? (
                <div className="empty">
                  <h3>עדיין אין פעילות</h3>
                  <p>כשתיצרו לקוחות ויתקבלו מסמכים ממורנינג, הם יופיעו כאן.</p>
                </div>
              ) : (
                <table className="table">
                  <tbody>
                    {(data?.recentActivity ?? []).map((a) => (
                      <tr key={a.id}>
                        <td style={{ width: '38%' }}>
                          {a.customerId ? <Link to={`/customers/${a.customerId}`} className="strong" style={{ color: 'var(--ink)' }}>{a.customerName}</Link> : <span className="muted">—</span>}
                        </td>
                        <td>{a.description}</td>
                        <td className="num muted small" title={fmtDateTime(a.createdAt)}>{fmtRelative(a.createdAt)}</td>
                      </tr>
                    ))}
                    {isLoading
                      ? Array.from({ length: 6 }, (_, i) => (
                          <tr key={i}>
                            <td colSpan={3}><span className="skeleton" style={{ width: `${50 + i * 7}%` }} /></td>
                          </tr>
                        ))
                      : null}
                  </tbody>
                </table>
              )}
            </section>

            <section className="panel">
              <div className="panel-head">
                <h2>לקוחות בפיגור תשלום</h2>
              </div>
              {data && data.topOverdue.length === 0 ? (
                <div className="empty" style={{ padding: 28 }}>
                  <p>אין חשבוניות באיחור.</p>
                </div>
              ) : (
                <table className="table">
                  <tbody>
                    {(data?.topOverdue ?? []).map((o) => (
                      <tr key={o.customerId}>
                        <td>
                          <Link to={`/customers/${o.customerId}?tab=invoices`} style={{ color: 'var(--ink)' }}>{o.businessName}</Link>
                          <span className="sub">מאז {fmtDate(o.oldestDue)}</span>
                        </td>
                        <td className="num" style={{ color: 'var(--bad)' }}>{fmtMoney(o.overdue, { round: true })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </div>
        </div>
      )}
    </>
  );
}

function Kpi({ label, value, meta, tone, to, loading }: { label: string; value: React.ReactNode; meta?: React.ReactNode; tone?: 'bad' | 'warn'; to?: string; loading?: boolean }) {
  const body = (
    <>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value" style={tone ? { color: `var(--${tone})` } : undefined}>
        {loading ? <span className="skeleton" style={{ width: 90, height: 20, marginTop: 5 }} /> : (value ?? '—')}
      </div>
      {meta ? <div className="kpi-meta">{meta}</div> : null}
    </>
  );
  return to ? (
    <Link to={to} className="kpi" style={{ color: 'inherit', textDecoration: 'none' }}>
      {body}
    </Link>
  ) : (
    <div className="kpi">{body}</div>
  );
}

/** Only shown when something needs attention – a healthy agent stays quiet (status is in the top bar). */
function AgentBanner({ agent }: { agent: AgentStatus }) {
  if (agent.overall === 'ok') return null;
  const s = agentTone(agent);
  const body: Record<string, string> = {
    setup_required: 'כדי שחשבוניות ממורנינג ייכנסו אוטומטית לתיקי הלקוחות, יש להגדיר חיבור ל-Gmail.',
    not_connected: 'כדי שחשבוניות ממורנינג ייכנסו אוטומטית לתיקי הלקוחות, חברו את תיבת הדואר העסקית.',
    auth_error: 'Google ביטלה את ההרשאה או שהיא פגה. מסמכים חדשים לא ייובאו עד לחיבור מחדש.',
    agent_offline: `הסוכן לא שלח סימן חיים${agent.agent.heartbeatAt ? ` מאז ${fmtDateTime(agent.agent.heartbeatAt)}` : ''}. מסמכים חדשים לא נקלטים.`,
    error: agent.agent.lastError ?? '',
  };
  return (
    <Callout
      tone={s.tone === 'bad' ? 'bad' : 'warn'}
      title={s.label}
      action={
        <Link to="/automation" className="btn sm">
          {agent.overall === 'auth_error' ? t.agent.reconnect : 'לסוכן המייל'}
        </Link>
      }
    >
      {body[agent.overall]}
    </Callout>
  );
}


/** Average gross margin of the invoices issued: overall, this month, last month, this year, and by month. */
function ProfitPanel() {
  const { data } = useProfitSummary();
  const months = data?.months.slice(0, 6) ?? [];
  return (
    <>
      <section className="kpis" aria-label="רווחיות">
        <ProfitKpi label="רווחיות החודש" p={data?.month} />
        <ProfitKpi label="רווחיות חודש שעבר" p={data?.prevMonth} />
        <ProfitKpi label="רווחיות השנה" p={data?.year} />
        <ProfitKpi label="רווחיות — כל החשבוניות" p={data?.all} />
        <Link to="/invoices" className="kpi" style={{ color: 'inherit', textDecoration: 'none' }}>
          <div className="kpi-label">לפי חודשים{data ? ` · יעד ${data.target}%` : ''}</div>
          <div className="row wrap" style={{ gap: 6, marginTop: 6 }}>
            {months.map((m) => (
              <span key={m.month} className="small" title={profitTitle(m)}>
                <span className="muted">{fmtMonth(m.month).split(' ')[0]}</span> <MarginBadge margin={m.margin} level={m.level} title={profitTitle(m)} />
              </span>
            ))}
            {data && !months.length ? <span className="faint small">אין עדיין חשבוניות עם עלות</span> : null}
          </div>
        </Link>
      </section>
    </>
  );
}
